import * as React from 'react'

import { Alert, Box, Button, Text, Tooltip } from '@factorialco/deprecated-design-system'
import { NewLeavePageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import FranceCalendarWarnings from 'modules/timeoff/components/FranceCalendarWarnings'
import Content from 'modules/timeoff/components/LeaveContent'
import LeaveInfo from 'modules/timeoff/components/LeaveInfo'
import useFetchLeaveTypes from 'modules/timeoff/hooks/useFetchLeaveTypes'
import useFetchStats from 'modules/timeoff/hooks/useFetchStats'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import allowances from 'modules/timeoff/store/collections/allowances'

import useButtonCopy from './useButtonCopy'
import useCreateLeave from './useCreateLeave'
import useCurrentEmployeeRole from './useCurrentEmployeeRole'
import useCustomFields from './useCustomFields'
import useForms from './useForms'
import useMedicalLeave from './useMedicalLeave'
import useModalProps from './useModalProps'
import useSelectedEmployees from './useSelectedEmployees'

interface Props {
  currentEmployee: Employee | null | undefined
  showEmployeeSelector?: boolean
  leaveTypeId?: string | null
  leaveYear?: string
  onClose: (year: number) => void
  bulkAdd?: boolean
  contractVersions: ContractVersions
  loading: boolean
  startDate?: string | undefined
  endDate?: string | undefined
  fromShifts?: boolean
}

const NewLeavePage = ({
  currentEmployee,
  showEmployeeSelector = false,
  leaveTypeId,
  leaveYear,
  onClose,
  bulkAdd = false,
  contractVersions,
  loading,
  startDate,
  endDate,
  fromShifts = false,
}: Props) => {
  const {
    company: { subscription },
  } = useCurrentSession()
  useAsyncData(() => {
    if (!bulkAdd) return Promise.resolve()

    return allowances.fetch()
  }, [])

  const fetchLeaveTypes = useFetchLeaveTypes({
    leaveTypeId: leaveTypeId !== undefined ? Number(leaveTypeId) : undefined,
    showEmployeeSelector,
    loadingLeave: false,
    employee: currentEmployee,
    active: true,
  })

  const { leaveTypes } = fetchLeaveTypes
  let { selectedLeaveType } = fetchLeaveTypes

  const { form, documentForm, employee, year } = useForms({
    leaveYear,
    selectedLeaveType,
    currentEmployee,
    startDate,
    endDate,
  })

  const { data } = useQuery(NewLeavePageDocument, {
    variables: { id: employee?.id },
    skip: !employee?.id,
  })

  const electronicSickLeaveEmployee = data?.employees?.employee

  const leaveId = form.get('leave_type_id').value
  const leaveType = leaveTypes.get(leaveId)
  const allowance = employee ? leaveType?.getAllowanceForEmployee(employee) : null

  const onCloseYear = () => {
    onClose(year)
  }

  const employeeRole = useCurrentEmployeeRole({
    currentEmployee,
    form,
  })

  const selectedEmployees = useSelectedEmployees({
    form,
    currentEmployee,
  })

  const labourUnits = useLabourUnits({
    form,
    bulkAdd,
  })

  const { fetchStats } = useFetchStats()

  const customFields = useCustomFields({
    employee,
    contractVersions,
  })

  const medicalLeave = useMedicalLeave({
    form,
    employee,
    leaveTypes,
  })

  const { onSubmit, isProcessing } = useCreateLeave({
    employee,
    form,
    documentForm,
    getFieldsValues: customFields.getValues,
    onLeaveSuccess: medicalLeave.fetchUpdates,
    onLeaveError: medicalLeave.onBackConfirm,
    fetchStats,
    onCloseYear,
    selectedEmployeeIds: selectedEmployees.selectedIds,
    bulkAdd,
  })

  const isLoading = isProcessing || loading

  const modalProps = useModalProps({
    currentEmployee,
    form,
    currentEmployeeRole: employeeRole,
    isMedical: medicalLeave.isMedical && medicalLeave.isConfirming,
  })

  const willAutoApprove = (employee ?? undefined)?.policy.can('approveLeaves')

  const regularButton = useButtonCopy({
    form,
    employee,
    labourUnits,
    currentEmployeeRole: employeeRole,
    selectedEmployeeIds: selectedEmployees.selectedIds,
  })

  const isSubmitEnabled = () => {
    const leaveTypeId = form.get('leave_type_id').value
    const halfDay = form.get('half_day').value
    const hourAmount = form.get('hours_amount_in_cents').value

    selectedLeaveType = leaveTypes.get(Number(leaveTypeId))

    if (!selectedLeaveType) return true

    const attachmentOptional = selectedLeaveType.isAttachmentOptional
    const medicalLeaveDocument = form.get('medical_leave_document').value
    const medicalLeaveDocumentFile = documentForm.get('file').value
    const isDetailsRequired = selectedLeaveType?.attributes.details_required
    const hasLeaveDescription = form.get('description').value
    const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount
    const hasStartOnDate = form.get('start_on').value

    if (isDetailsRequired) {
      return selectedLeaveType?.isMedicalLeave
        ? hasLeaveDescription && (attachmentOptional || medicalLeaveDocument)
        : hasLeaveDescription && (attachmentOptional || medicalLeaveDocumentFile)
    }

    if (absurdStartTime) return false

    if (selectedLeaveType?.isMedicalLeave) {
      return attachmentOptional || medicalLeaveDocument
    }

    return hasStartOnDate && (attachmentOptional || medicalLeaveDocumentFile)
  }

  const { approvalFlows } = useAccessApprovalFlows('timeoff', currentEmployee?.attributes.access_id)

  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    (currentEmployee?.policy.can('approveLeaves') || approvalFlows.length > 0)
  const isSickLeave = () => {
    const leaveTypeId = form.get('leave_type_id').value
    selectedLeaveType = leaveTypes.get(Number(leaveTypeId))
    return selectedLeaveType?.isSick
  }

  const canApplyForEAU =
    hasFeature('timeoff_electronic_sick_leave') &&
    isSickLeave() &&
    electronicSickLeaveEmployee?._policies?.electronicSickLeave.can

  const setSkipEauCheckValue = () => {
    if (canApplyForEAU) {
      if (form.fields.skip_eau_check?.value !== true) {
        form.get('skip_eau_check').set(false)
      }
    } else {
      form.get('skip_eau_check').set(null)
    }
  }

  const onSubmitTracked = () => {
    // The user will only get this error is he's been playing with the HTML
    // inspector and removed the disabled attribute from the submit button
    if (!isSubmitEnabled()) {
      return
    }
    track(trackingEvents.timeoff.addTimeOffSubmit, {
      from_shifts: fromShifts,
    })

    setSkipEauCheckValue()
    onSubmit()
  }

  useEffect(() => {
    track(trackingEvents.timeoff.addTimeOffStart, {
      from_shifts: fromShifts,
    })
  }, [fromShifts])

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={() => onCloseYear()}>
      <Modal
        illustration={modalProps.illustration}
        title={modalProps.title}
        description={modalProps.description}
        onClose={() => onCloseYear()}
        onSubmit={onSubmitTracked}
        footer={
          <>
            {medicalLeave.isMedical && medicalLeave.isConfirming && (
              <Button
                type="secondary"
                onClick={medicalLeave.onBackConfirm}
                disabled={isLoading || !currentEmployee}
              >
                {i18n.t('common.back')}
              </Button>
            )}

            {medicalLeave.isMedical && !medicalLeave.isConfirming && (
              <Button
                onClick={medicalLeave.onConfirm}
                disabled={!currentEmployee || !isSubmitEnabled()}
              >
                {i18n.t('time_off.medical_leave.request')}
              </Button>
            )}

            {medicalLeave.isMedical && medicalLeave.isConfirming && (
              <Button disabled={isLoading || !currentEmployee} submit>
                {i18n.t('time_off.medical_leave.request_confirm')}
              </Button>
            )}
            {!medicalLeave.isMedical && !regularButton.tooltip && (
              <Button
                submit
                disabled={isLoading || selectedEmployees.disabled || !isSubmitEnabled()}
              >
                {regularButton.button}
              </Button>
            )}

            {!medicalLeave.isMedical && !!regularButton.tooltip && (
              <Tooltip
                attachment="bottom center"
                targetAttachment="top center"
                type="brand"
                size="normal"
                open={regularButton.tooltipOpen}
                toggle={regularButton.toggleTooltip}
              >
                <Button disabled={isLoading || selectedEmployees.disabled} submit>
                  {regularButton.button}
                </Button>
                <Tooltip.Pad>{regularButton.tooltip}</Tooltip.Pad>
              </Tooltip>
            )}
          </>
        }
      >
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Content
              employee={employee}
              leaveTypes={leaveTypes}
              form={form}
              year={year}
              documentForm={documentForm}
              customFieldsForms={customFields.forms}
              showEmployeeSelector={showEmployeeSelector}
              onSelectEmployee={selectedEmployees.onSelect}
              selectedEmployeeIds={selectedEmployees.selectedIds}
              bulkAdd={bulkAdd}
              electronicSickLeaveEmployee={electronicSickLeaveEmployee}
              contractVersions={contractVersions}
              labourUnits={labourUnits}
            />
            <Box gap="s8">
              {allowance && (
                <FranceCalendarWarnings allowance={allowance} labourUnits={labourUnits} />
              )}
              <LeaveInfo form={form} />
              {canDelegateApprover && currentEmployee && (
                <DelegateApproverSelector
                  approver={null}
                  category="timeoff"
                  employee={currentEmployee}
                  field={form.get('delegated_approver_id')}
                  startsOn={form.get('start_on').value}
                  finishOn={form.get('finish_on').value}
                />
              )}
              {willAutoApprove && (
                <Alert color="warning">
                  <Box flexDirection="row" justifyContent="center">
                    <Text.Normal>
                      {i18n.t('time_off.employee.add_new.auto_approval_flow')}
                    </Text.Normal>
                  </Box>
                </Alert>
              )}
            </Box>
          </>
        )}
      </Modal>
    </ExpiredTrialChecker>
  )
}

export default observer(NewLeavePage)
